import React, { useState } from 'react';
import { Share2, Copy, Check, MessageCircle, Twitter, Linkedin, Facebook, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const ShareButtons = ({ call, compact = false }) => {
  const [copied, setCopied] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  // Use SEO-friendly slug URL when available
  const baseUrl = window.location.origin;
  const shareUrl = call.slug 
    ? `${baseUrl}/chamada/${call.slug}` 
    : `${baseUrl}/call/${call.call_id}`;
  
  // Format deadline
  const formatDeadline = () => {
    if (call.continuous_flow) return 'Fluxo Contínuo';
    if (call.deadline) return new Date(call.deadline).toLocaleDateString('pt-BR');
    return 'Não informado';
  };

  // Share text with requested format: Title, Journal, Qualis, Deadline, Link
  const shareTitle = `${call.title} - Quero Publicar`;
  const shareText = `📚 *${call.title}*\n📰 ${call.journal_name || 'Revista não informada'}\n⭐ QUALIS ${call.qualis_classification}\n📅 Prazo: ${formatDeadline()}\n\n🔗 Confira: `;
  
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(shareText);

  // Share URLs for each platform
  const shareLinks = {
    whatsapp: `https://api.whatsapp.com/send?text=${encodedText}${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}${shareUrl}`);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Erro ao copiar');
    }
  };

  const handleShare = (platform) => {
    window.open(shareLinks[platform], '_blank', 'width=600,height=400');
    setShowOptions(false);
  };

  // Native share API (mobile)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          setShowOptions(true);
        }
      }
    } else {
      setShowOptions(true);
    }
  };

  if (compact) {
    return (
      <div className="relative">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleNativeShare}
          className="text-[#4B4C52] hover:text-[#002FA7]"
          data-testid={`share-btn-${call.call_id}`}
        >
          <Share2 className="h-4 w-4" />
        </Button>

        {showOptions && (
          <>
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setShowOptions(false)}
            />
            <div className="absolute right-0 top-8 z-50 bg-white border border-[#E4E4E7] rounded-lg shadow-lg p-2 min-w-[200px]">
              <p className="text-xs font-semibold text-[#4B4C52] px-2 py-1 mb-1">Compartilhar via</p>
              
              <button
                onClick={() => handleShare('whatsapp')}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-md text-sm"
              >
                <MessageCircle className="h-4 w-4 text-green-500" />
                WhatsApp
              </button>
              
              <button
                onClick={() => handleShare('telegram')}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-md text-sm"
              >
                <Send className="h-4 w-4 text-blue-500" />
                Telegram
              </button>
              
              <button
                onClick={() => handleShare('twitter')}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-md text-sm"
              >
                <Twitter className="h-4 w-4 text-sky-500" />
                Twitter / X
              </button>
              
              <button
                onClick={() => handleShare('linkedin')}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-md text-sm"
              >
                <Linkedin className="h-4 w-4 text-blue-700" />
                LinkedIn
              </button>
              
              <button
                onClick={() => handleShare('facebook')}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-md text-sm"
              >
                <Facebook className="h-4 w-4 text-blue-600" />
                Facebook
              </button>
              
              <hr className="my-1" />
              
              <button
                onClick={copyToClipboard}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-md text-sm"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4 text-gray-500" />
                )}
                {copied ? 'Copiado!' : 'Copiar Link'}
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // Full version with visible buttons
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs text-[#4B4C52] mr-1">Compartilhar:</span>
      
      <button
        onClick={() => handleShare('whatsapp')}
        className="p-2 rounded-full hover:bg-green-50 transition-colors"
        title="WhatsApp"
      >
        <MessageCircle className="h-4 w-4 text-green-500" />
      </button>
      
      <button
        onClick={() => handleShare('telegram')}
        className="p-2 rounded-full hover:bg-blue-50 transition-colors"
        title="Telegram"
      >
        <Send className="h-4 w-4 text-blue-500" />
      </button>
      
      <button
        onClick={() => handleShare('twitter')}
        className="p-2 rounded-full hover:bg-sky-50 transition-colors"
        title="Twitter / X"
      >
        <Twitter className="h-4 w-4 text-sky-500" />
      </button>
      
      <button
        onClick={() => handleShare('linkedin')}
        className="p-2 rounded-full hover:bg-blue-50 transition-colors"
        title="LinkedIn"
      >
        <Linkedin className="h-4 w-4 text-blue-700" />
      </button>
      
      <button
        onClick={() => handleShare('facebook')}
        className="p-2 rounded-full hover:bg-blue-50 transition-colors"
        title="Facebook"
      >
        <Facebook className="h-4 w-4 text-blue-600" />
      </button>
      
      <button
        onClick={copyToClipboard}
        className="p-2 rounded-full hover:bg-gray-100 transition-colors"
        title="Copiar Link"
      >
        {copied ? (
          <Check className="h-4 w-4 text-green-500" />
        ) : (
          <Copy className="h-4 w-4 text-gray-500" />
        )}
      </button>
    </div>
  );
};

export default ShareButtons;
